// «Кабинет Совета» (M4, OG-4) — клиентский зал: встречи, состав, раунды.
//
// Контракт серверной части — council.routes.ts: встреча хранит состав, вопросы
// клиента и эмитированные реплики; раунд либо закрывается сразу (200), либо
// ждёт человека (202: согласие клиента на новый состав / проверка куратора).
// Лента собирается здесь: сервер отдаёт обе стороны разговора отдельными
// списками, и связывает их `run_id` — порядок восстанавливаем по раунду,
// а не по времени (см. buildFeed).
import './council.css';
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { showRegistrationPrompt } from '../shared/navigation';

/**
 * Функция участника Совета — это Тип его Аватара (ADR-008 §3.2 п. 3). Прежде
 * их было четыре и клиент назначал их руками; теперь шесть, и они следуют из
 * паспорта — выбирать нечего.
 */
/**
 * Р-26 (владелец 2026-08-23, вариант А; Хартия §6 «О сцене»): Тип Аватара в
 * клиентскую сцену Совета не выходит — ни полем в DTO, ни бейджем. Клиент
 * видит имя, лицо и одно служебное обстоятельство: есть ли у Аватара личность
 * (без неё он не говорит). Тип видят куратор и администратор.
 */
interface CouncilSpeaker {
  speaker_avatar_ref: string;
  /** Есть ли у Аватара личность (паспорт); без неё за столом он молчит. */
  has_personality?: boolean;
  display_name?: string;
}

interface CouncilMeeting {
  meeting_id: string;
  title: string;
  status: string;
  composition: CouncilSpeaker[] | null;
  confirmed_speakers: string[] | null;
  created_at?: string;
}

interface CouncilContribution {
  run_id?: string;
  slot_index: number;
  speaker_avatar_ref: string;
  content: string;
  references?: Array<{ kind: 'build_on' | 'contrast'; target_slot: number }>;
  /** 'synthesis' — сведение позиций от лица Главы Совета (Совет-2); отсутствие — реплика. */
  kind?: 'reply' | 'synthesis';
  created_at?: string;
}

/** Вопрос человека: с ним лента перестаёт быть монологом Совета. */
interface CouncilClientMessage {
  run_id: string;
  content: string;
  created_at?: string;
}

interface CouncilConfig {
  enabled: boolean;
  curator_review_enabled: boolean;
  /** Сколько участников выступит в раунде (Р-22); состав может быть шире. */
  max_slots?: number;
}

interface AvatarProfileOption {
  avatar_id: string;
  label?: string;
  /** Тип Аватара из паспорта; пусто — личности нет, за стол Совета нельзя. */
  er_type_id?: string;
}

/**
 * Оттенки мест за столом — нейтральная палитра по участнику (детерминированно
 * от ссылки Аватара), а не по Типу: цвет различает людей за столом и ничего
 * не сообщает о них (Р-26).
 */
const SEAT_HUES = [
  'var(--cnc-unsleeping)', 'var(--cnc-decoder)', 'var(--cnc-warrior)',
  'var(--cnc-guardian)', 'var(--cnc-narod)', 'var(--cnc-shaman)',
];

/** Участник без личности: Совет им не говорит, но из состава его не прячут. */
const NO_PERSONALITY_META = {
  label: 'Без личности', desc: 'У Аватара нет паспорта — он не может говорить в Совете',
  hueVar: 'var(--cnc-unknown)',
};

function seatMeta(s: { speaker_avatar_ref: string; has_personality?: boolean }): { label: string; desc: string; hueVar: string } {
  if (s.has_personality === false) return NO_PERSONALITY_META;
  let h = 0;
  for (const ch of s.speaker_avatar_ref) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { label: '', desc: '', hueVar: SEAT_HUES[h % SEAT_HUES.length] };
}

const esc = (s: unknown): string => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

let councilConfig: CouncilConfig | null = null;
/** Мест за столом — столько же, сколько потолок платформы по репликам (COUNCIL_MAX_SLOTS). */
const MAX_SEATS = 6;
let profileCache: AvatarProfileOption[] = [];
let currentMeeting: CouncilMeeting | null = null;
/** runId раунда, ждущего согласия клиента, — цель POST /consent. */
let pendingConsentRunId: string | null = null;
let roundInFlight = false;
/** Черновой состав в модалке (создание встречи или смена состава). */
let builderSpeakers: CouncilSpeaker[] = [];
let builderMode: 'create' | 'edit' = 'create';

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + state.authToken };
}

async function api<T>(path: string, init?: RequestInit): Promise<{ ok: boolean; status: number; body: T }> {
  const r = await fetch(path, { headers: authHeaders(), ...init });
  const body = await r.json().catch(() => ({})) as T;
  return { ok: r.ok, status: r.status, body };
}

function speakerName(s: CouncilSpeaker): string {
  if (s.display_name) return s.display_name;
  const profile = profileCache.find((p) => p.avatar_id === s.speaker_avatar_ref);
  return profile?.label || s.speaker_avatar_ref;
}

function speakerInitial(s: CouncilSpeaker): string {
  return (speakerName(s).trim()[0] || '?').toUpperCase();
}

/**
 * Состояние встречи приходит из БД: `open` (по умолчанию) и `closed`. Раньше
 * зал сравнивал статус с несуществующим `active` и потому рисовал «Закрыта» на
 * всех встречах подряд. Закрытой считаем только явно закрытую — незнакомое
 * значение (старая строка, будущий статус) читается как рабочая встреча.
 */
function isMeetingClosed(meeting: { status?: string } | null): boolean {
  return meeting?.status === 'closed';
}

// ---------- Пункт меню ----------

/**
 * Пункт «Совет» появляется только когда услуга включена админом
 * (council_settings.enabled + живой OG-контур): выключенный кабинет не должен
 * маячить в меню мёртвой кнопкой. Зовётся из auth после входа.
 */
export async function refreshCouncilNav(): Promise<void> {
  const nav = $('nav-council');
  if (!nav) return;
  if (!state.authToken) {
    nav.classList.add('hidden');
    return;
  }
  try {
    const { ok, body } = await api<CouncilConfig>('/api/council/config');
    councilConfig = ok ? body : null;
    nav.classList.toggle('hidden', !councilConfig?.enabled);
  } catch {
    nav.classList.add('hidden');
  }
}

// ---------- Зал: список встреч ----------

export async function loadCouncilView(): Promise<void> {
  showHall();
  const grid = $('cnc-meetings-grid');
  const status = $('cnc-hall-status');
  if (!grid || !status) return;
  status.classList.add('hidden');

  try {
    const cfg = await api<CouncilConfig>('/api/council/config');
    councilConfig = cfg.ok ? cfg.body : null;
  } catch { councilConfig = null; }

  if (!councilConfig?.enabled) {
    grid.innerHTML = '';
    status.textContent = 'Кабинет Совета сейчас недоступен — услуга не включена. Загляните позже.';
    status.classList.remove('hidden');
    return;
  }

  grid.innerHTML = '<div class="cnc-empty">Собираем встречи…</div>';
  const { ok, status: http, body } = await api<{ meetings?: CouncilMeeting[]; detail?: string }>('/api/council/meetings');
  if (!ok) {
    grid.innerHTML = '';
    status.textContent = http === 403
      ? 'Кабинет Совета доступен после регистрации: встречи и их материалы принадлежат личному кабинету.'
      : (body.detail || 'Не удалось загрузить встречи.');
    status.classList.remove('hidden');
    return;
  }
  renderMeetings(body.meetings || []);
}

function renderMeetings(meetings: CouncilMeeting[]): void {
  const grid = $('cnc-meetings-grid');
  const count = $('cnc-meetings-count');
  if (!grid) return;
  if (count) count.textContent = meetings.length ? String(meetings.length) : '';

  if (!meetings.length) {
    grid.innerHTML = '<div class="cnc-empty">Встреч пока нет. Соберите первый Совет — выберите участников и задайте вопрос.</div>';
    return;
  }

  grid.innerHTML = meetings.map((m, i) => {
    const speakers = m.composition || [];
    const chips = speakers.slice(0, 4).map((s) => {
      const meta = seatMeta(s);
      return `<span class="cnc-chip" title="${esc(meta.label || speakerName(s))}">`
        + `<span class="cnc-chip-dot" style="--chip-hue:${meta.hueVar}">${esc(speakerInitial(s))}</span>`
        + `${esc(speakerName(s))}</span>`;
    }).join('');
    const more = speakers.length > 4 ? `<span class="cnc-chip">+${speakers.length - 4}</span>` : '';
    const date = m.created_at ? new Date(m.created_at).toLocaleDateString('ru-RU') : '';
    const pill = isMeetingClosed(m)
      ? '<span class="cnc-pill cnc-pill--closed">Закрыта</span>'
      : '<span class="cnc-pill cnc-pill--active">Открыта</span>';
    return `<button type="button" class="cnc-meeting-card" data-meeting="${esc(m.meeting_id)}" style="animation-delay:${Math.min(i * 60, 420)}ms">`
      + `<h4>${esc(m.title || 'Встреча Совета')}</h4>`
      + `<div class="cnc-chip-row">${chips}${more}</div>`
      + `<div class="cnc-meeting-meta"><span>${esc(date)}</span>${pill}</div>`
      + '</button>';
  }).join('');

  grid.querySelectorAll<HTMLElement>('[data-meeting]').forEach((card) => {
    card.addEventListener('click', () => void openMeeting(card.dataset.meeting || ''));
  });
}

function showHall(): void {
  currentMeeting = null;
  pendingConsentRunId = null;
  // Баннер ожидания живёт в ленте и вместе с ней остаётся в скрытом зале
  // встречи: снимаем его сами, иначе частый опрос куратора грел бы сервер
  // из раздела, где показывать решение уже некуда.
  hideCuratorBanner();
  $('cnc-screen-chamber')?.classList.add('hidden');
  $('cnc-screen-hall')?.classList.remove('hidden');
}

// ---------- Зал встречи ----------

/**
 * Лента встречи: вопрос клиента и ответившие ему реплики приходят разными
 * списками, но человек читает один разговор.
 *
 * Сводим не по времени, а по раунду. `created_at` реплики — это время
 * черновика, а второй слот генерируется только после одобрения куратора:
 * куратор ответил через сутки, клиент за это время спросил о другом — и по
 * чистой хронологии реплика №2 старого раунда встала бы под новым вопросом и
 * читалась как ответ на него. То же при двух раундах внахлёст (`startRound`
 * второго раунда не запрещает). Поэтому реплики идут следом за породившим их
 * вопросом; раунды между собой — по времени своего начала, а внутри раунда —
 * по номеру слота (очередь раунда).
 *
 * У записей до C-1 вопроса нет вовсе (тогда он не хранился) — такая реплика
 * становится группой сама по себе и встаёт по собственному времени.
 */
type FeedEntry =
  | { at: string; order: number; kind: 'client'; message: CouncilClientMessage }
  | { at: string; order: number; kind: 'speaker'; contribution: CouncilContribution };

interface FeedGroup { at: string; seq: number; entries: FeedEntry[] }

function buildFeed(
  clientMessages: CouncilClientMessage[],
  contributions: CouncilContribution[],
): FeedEntry[] {
  const groups = new Map<string, FeedGroup>();
  const groupFor = (key: string, at: string): FeedGroup => {
    const known = groups.get(key);
    if (!known) {
      const fresh: FeedGroup = { at, seq: groups.size, entries: [] };
      groups.set(key, fresh);
      return fresh;
    }
    // Раунд начался раньше, чем показалось: якорь группы — самое раннее время.
    if (at && (!known.at || at.localeCompare(known.at) < 0)) known.at = at;
    return known;
  };

  clientMessages.forEach((m, i) => {
    const at = String(m.created_at || '');
    groupFor(m.run_id ? `run:${m.run_id}` : `msg:${i}`, at)
      .entries.push({ at, order: -1, kind: 'client', message: m });
  });
  contributions.forEach((c, i) => {
    const at = String(c.created_at || '');
    groupFor(c.run_id ? `run:${c.run_id}` : `sole:${i}`, at)
      .entries.push({ at, order: c.slot_index, kind: 'speaker', contribution: c });
  });

  return [...groups.values()]
    .sort((a, b) => a.at.localeCompare(b.at) || a.seq - b.seq)
    .flatMap((g) => g.entries.sort((a, b) => a.order - b.order || a.at.localeCompare(b.at)));
}

/**
 * Перерисовка ленты не должна стирать ожидание.
 *
 * Ожидания раунда (согласие клиента на состав, проверка куратора) в материалах
 * встречи не лежат: непроверенной реплики там нет, а карточка согласия живёт
 * только в памяти вкладки. Поэтому при каждом открытии встречи спрашиваем
 * состояние её раундов сразу же — не дожидаясь тика сторожа: он ходит раз в
 * минуту, а при выключенном гейте куратора не поднимается вовсе. Без этого
 * человек до минуты видел обычную встречу с открытым полем ввода и мог
 * отправить второй платный вопрос, не зная, что первый ещё на проверке; а
 * карточка согласия, снятая перерисовкой, не возвращалась никогда, и раунд
 * висел до срока.
 */
async function restoreMeetingWaits(): Promise<void> {
  if (!currentMeeting) return;
  await refreshCouncilRounds({ freshFeed: true });
}

async function openMeeting(meetingId: string): Promise<void> {
  if (!meetingId) return;
  const { ok, body } = await api<CouncilMeeting & {
    contributions?: CouncilContribution[];
    client_messages?: CouncilClientMessage[];
    detail?: string;
  }>(
    `/api/council/meetings/${encodeURIComponent(meetingId)}`,
  );
  if (!ok) {
    showToast((body as { detail?: string }).detail || 'Не удалось открыть встречу', { variant: 'error' });
    return;
  }
  currentMeeting = body;
  $('cnc-screen-hall')?.classList.add('hidden');
  $('cnc-screen-chamber')?.classList.remove('hidden');

  const title = $('cnc-chamber-title');
  if (title) title.textContent = body.title || 'Встреча Совета';
  applyMeetingState();
  renderSeats();

  const feed = $('cnc-feed');
  if (feed) {
    feed.innerHTML = '';
    const entries = buildFeed(body.client_messages || [], body.contributions || []);
    if (!entries.length) {
      feed.innerHTML = '<div class="cnc-empty">Совет ещё не говорил. Задайте вопрос — участники ответят по очереди, каждый из своей функции.</div>';
    } else {
      entries.forEach((entry) => {
        if (entry.kind === 'client') appendClientMessage(entry.message.content);
        else appendContribution(entry.contribution);
      });
    }
  }
  // Ожидания прошлой встречи на экране не оставляем, но и не гасим вслепую:
  // состояние раундов этой встречи спрашиваем тут же.
  pendingConsentRunId = null;
  hideConsent();
  hideCuratorBanner();
  updateComposerHint();
  $('cnc-message')?.focus();
  await restoreMeetingWaits();
  await loadArtifacts();
}

// ---------- Итоги встречи (Совет-2, артефакты AE3-ADR-14 §7) ----------

type CouncilArtifactKind = 'summary_report' | 'recording_transcript' | 'hypotheses_for_review'
  | 'open_questions' | 'recommendations' | 'next_meeting_plan';

interface CouncilArtifactDto {
  kind: CouncilArtifactKind;
  version: number;
  status: 'filled' | 'empty_explicit';
  reason: string | null;
  payload: { text?: string; items?: string[] } | null;
  created_at: string;
}

const ARTIFACT_TITLES: Record<CouncilArtifactKind, string> = {
  summary_report: 'Резюме встречи',
  open_questions: 'Открытые вопросы',
  recommendations: 'Рекомендации (необязательные)',
  next_meeting_plan: 'Следующая встреча',
  hypotheses_for_review: 'Гипотезы о задаче — на проверку',
  recording_transcript: 'Запись встречи',
};

/** Порядок показа — читаемый сверху вниз, недоступные разделы — в конце. */
const ARTIFACT_ORDER: CouncilArtifactKind[] = [
  'summary_report', 'hypotheses_for_review', 'open_questions', 'recommendations', 'next_meeting_plan',
  'recording_transcript',
];

const ARTIFACT_EMPTY_REASONS: Record<string, string> = {
  unavailable_in_version: 'В этой версии раздел недоступен.',
  nothing_to_report: 'Совету нечего сюда добавить по этой встрече.',
};

function renderArtifacts(data: { pending: boolean; artifacts: CouncilArtifactDto[] }): void {
  const host = $('cnc-artifacts-list');
  const status = $('cnc-artifacts-status');
  const btn = $<HTMLButtonElement>('cnc-artifacts-request');
  if (!host || !status) return;
  const byKind = new Map(data.artifacts.map((a) => [a.kind, a]));
  const version = Math.max(0, ...data.artifacts.map((a) => a.version));
  if (btn) btn.disabled = data.pending;
  status.textContent = data.pending
    ? 'Итоги собраны и ждут проверки куратора — появятся здесь после неё.'
    : version > 0
      ? `Версия итогов ${version}. Подвести итоги заново можно в любой момент — прежние версии сохраняются.`
      : 'Итоги ещё не подводились.';
  if (!data.artifacts.length) { host.innerHTML = ''; return; }
  host.innerHTML = ARTIFACT_ORDER.map((kind) => {
    const a = byKind.get(kind);
    if (!a) return '';
    const title = `<div class="cnc-artifact-title">${esc(ARTIFACT_TITLES[kind])}</div>`;
    if (a.status !== 'filled' || !a.payload) {
      const why = ARTIFACT_EMPTY_REASONS[a.reason || ''] || 'Раздел пуст.';
      return `<div class="cnc-artifact cnc-artifact--empty">${title}<div class="cnc-artifact-body">${esc(why)}</div></div>`;
    }
    const body = Array.isArray(a.payload.items)
      ? `<ul>${a.payload.items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>`
      : `<div class="cnc-artifact-body">${esc(a.payload.text || '')}</div>`;
    return `<div class="cnc-artifact">${title}${body}</div>`;
  }).join('');
}

async function loadArtifacts(): Promise<void> {
  const meeting = currentMeeting;
  const section = $('cnc-artifacts');
  if (!meeting || !section) return;
  const { ok, body } = await api<{ pending: boolean; artifacts: CouncilArtifactDto[] }>(
    `/council/meetings/${encodeURIComponent(meeting.meeting_id)}/artifacts`);
  if (!ok) { section.classList.add('hidden'); return; }
  // Раздел виден, когда Совет уже говорил (есть что подводить) либо итоги уже есть.
  const spoke = Boolean($('cnc-feed')?.querySelector('.cnc-msg--speaker')) || body.artifacts.length > 0 || body.pending;
  section.classList.toggle('hidden', !spoke);
  renderArtifacts(body);
}

async function requestArtifacts(): Promise<void> {
  const meeting = currentMeeting;
  const btn = $<HTMLButtonElement>('cnc-artifacts-request');
  if (!meeting) return;
  if (btn) btn.disabled = true;
  try {
    const { ok, status, body } = await api<{ status?: string; waiting_for?: string | null; detail?: string }>(
      `/council/meetings/${encodeURIComponent(meeting.meeting_id)}/artifacts`, { method: 'POST' });
    if (!ok) {
      if (btn) btn.disabled = false;
      showToast(body?.detail || (status === 402 ? 'Не хватает средств на сборку итогов.' : 'Не удалось запросить итоги.'), { variant: 'warning' });
      return;
    }
    showToast(body.waiting_for === 'artifact_review'
      ? 'Глава Совета собрал итоги — они дойдут до вас после проверки куратором.'
      : 'Запрос итогов принят.');
    // Кнопку дальше ведёт renderArtifacts: пока сборка ждёт куратора — выключена.
    await loadArtifacts();
  } catch {
    if (btn) btn.disabled = false;
    showToast('Сеть недоступна — итоги не запрошены.', { variant: 'error' });
  }
}

function renderSeats(): void {
  const host = $('cnc-seats');
  if (!host || !currentMeeting) return;
  const confirmed = new Set(currentMeeting.confirmed_speakers || []);
  host.innerHTML = (currentMeeting.composition || []).map((s) => {
    const meta = seatMeta(s);
    const pending = confirmed.size > 0 && !confirmed.has(s.speaker_avatar_ref);
    const sub = [meta.label, pending ? 'ждёт согласия' : ''].filter(Boolean).join(' · ');
    return `<div class="cnc-seat${pending ? ' cnc-seat--pending' : ''}" style="--seat-hue:${meta.hueVar}"`
      + ` data-seat="${esc(s.speaker_avatar_ref)}" title="${pending ? 'Ждёт вашего согласия' : esc(meta.desc || speakerName(s))}">`
      + `<span class="cnc-seat-avatar">${esc(speakerInitial(s))}</span>`
      + `<span><span class="cnc-seat-name">${esc(speakerName(s))}</span>`
      + (sub ? `<br><span class="cnc-seat-func">${esc(sub)}</span>` : '')
      + '</span></div>';
  }).join('');
}

function setSeatsSpeaking(on: boolean): void {
  document.querySelectorAll('#cnc-seats .cnc-seat').forEach((seat) => {
    seat.classList.toggle('cnc-seat--speaking', on);
  });
}

// ---------- Управление встречей: название, закрытие, удаление ----------

/**
 * Шапка и строка реплики по состоянию встречи. Закрытая встреча остаётся
 * читаемой — лента, состав и материалы на месте; недоступен только новый
 * раунд. Человек должен видеть это до нажатия, а не узнавать из ошибки: гейт
 * стоит на сервере, здесь — честная подсказка.
 */
function applyMeetingState(): void {
  const closed = isMeetingClosed(currentMeeting);

  const pill = $('cnc-chamber-status');
  if (pill) {
    pill.textContent = closed ? 'Закрыта' : 'Открыта';
    pill.classList.toggle('cnc-pill--closed', closed);
    pill.classList.toggle('cnc-pill--active', !closed);
    pill.classList.toggle('hidden', !currentMeeting);
  }

  const toggleLabel = $('cnc-toggle-status-label');
  if (toggleLabel) toggleLabel.textContent = closed ? 'Открыть' : 'Закрыть';
  const toggle = $('cnc-toggle-status');
  toggle?.setAttribute('aria-label', closed ? 'Открыть встречу заново' : 'Закрыть встречу');
  // На узком экране от кнопки остаётся один значок: замок должен показывать не
  // «здесь про замок», а что именно сделает нажатие (см. council.css).
  toggle?.classList.toggle('cnc-ghost-btn--reopen', closed);

  const input = $<HTMLTextAreaElement>('cnc-message');
  if (input) {
    input.disabled = closed;
    input.placeholder = closed
      ? 'Встреча закрыта — Совет здесь больше не говорит'
      : 'Скажите Совету…';
    input.closest('.cnc-composer')?.classList.toggle('cnc-composer--locked', closed);
  }
  const send = $<HTMLButtonElement>('cnc-send');
  if (send) send.disabled = closed;
  updateComposerHint();
}

/**
 * Правка встречи на месте: перечитывать всю ленту ради названия незачем.
 * Возвращает число снятых раундов (`null` — правка не удалась): закрытие
 * встречи останавливает недоигранный раунд, и человеку об этом надо сказать.
 */
async function patchMeeting(patch: { title?: string; status?: 'open' | 'closed' }): Promise<number | null> {
  const meeting = currentMeeting;
  if (!meeting) return null;
  const { ok, body } = await api<{
    title?: string;
    meeting_status?: string;
    cancelled_rounds?: number;
    detail?: string;
  }>(`/api/council/meetings/${encodeURIComponent(meeting.meeting_id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!ok) {
    showToast(body.detail || 'Не удалось изменить встречу.', { variant: 'error' });
    return null;
  }

  meeting.title = body.title ?? meeting.title;
  meeting.status = body.meeting_status ?? meeting.status;
  const title = $('cnc-chamber-title');
  if (title) title.textContent = meeting.title || 'Встреча Совета';
  if (isMeetingClosed(meeting)) {
    // Закрытие снимает недоигранные раунды — ждать больше нечего, и ожидания
    // на экране висеть не должны.
    pendingConsentRunId = null;
    hideConsent();
    hideCuratorBanner();
    forgetMeetingRounds(meeting.meeting_id);
  }
  applyMeetingState();
  return Number(body.cancelled_rounds ?? 0);
}

function openRename(): void {
  if (!currentMeeting) return;
  const input = $<HTMLInputElement>('cnc-rename-input');
  if (input) input.value = currentMeeting.title || '';
  $('cnc-rename-modal')?.classList.remove('hidden');
  input?.focus();
}

function closeRename(): void {
  $('cnc-rename-modal')?.classList.add('hidden');
}

async function saveRename(): Promise<void> {
  const title = $<HTMLInputElement>('cnc-rename-input')?.value.trim() || '';
  const btn = $<HTMLButtonElement>('cnc-rename-save');
  if (btn) btn.disabled = true;
  try {
    if (await patchMeeting({ title }) !== null) {
      closeRename();
      showToast('Название встречи обновлено.');
    }
  } finally {
    if (btn) btn.disabled = false;
  }
}

/**
 * Сколько раундов этой встречи ещё живы. Закрытие встречи снимает их на
 * сервере, а снятая реплика уже сгенерирована и оплачена — спрашивать
 * подтверждение имеет смысл только когда терять действительно есть что.
 * Не смогли узнать — не пугаем зря и закрываем как обычно.
 */
async function countLiveRounds(meetingId: string): Promise<number> {
  try {
    const { ok, body } = await api<{ rounds?: CouncilPendingRound[] }>('/api/council/rounds/pending');
    if (!ok) return 0;
    return (body.rounds || []).filter((r) => r.meeting_id === meetingId
      && (r.status === 'waiting' || r.status === 'running')).length;
  } catch {
    return 0;
  }
}

async function toggleMeetingStatus(): Promise<void> {
  const meeting = currentMeeting;
  if (!meeting) return;
  const closing = !isMeetingClosed(meeting);
  const btn = $<HTMLButtonElement>('cnc-toggle-status');
  if (btn) btn.disabled = true;
  try {
    // Закрытие с живым раундом необратимо теряет уже оплаченную реплику —
    // спрашиваем так же честно, как перед удалением встречи.
    if (closing && await countLiveRounds(meeting.meeting_id) > 0) {
      openCloseConfirm();
      return;
    }
    await applyMeetingStatus(closing);
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** Само переключение статуса: зовётся и напрямую, и после подтверждения. */
async function applyMeetingStatus(closing: boolean): Promise<void> {
  const cancelled = await patchMeeting({ status: closing ? 'closed' : 'open' });
  if (cancelled === null) return;
  if (!closing) {
    showToast('Встреча снова открыта — можно продолжать разговор.');
    return;
  }
  showToast(cancelled > 0
    ? 'Встреча закрыта, незаконченный раунд остановлен: этой реплики Совет не скажет, '
      + 'и оплату это не отменяет. Остальные материалы на месте — открыть встречу заново можно в любой момент.'
    : 'Встреча закрыта. Материалы остались — открыть её заново можно в любой момент.',
  { duration: cancelled > 0 ? 12000 : undefined });
}

function openCloseConfirm(): void {
  if (!currentMeeting) return;
  $('cnc-close-modal')?.classList.remove('hidden');
  // Фокус на безопасном действии: случайный Enter не должен обрывать раунд.
  $('cnc-close-cancel')?.focus();
}

function closeCloseConfirm(): void {
  $('cnc-close-modal')?.classList.add('hidden');
}

async function confirmCloseMeeting(): Promise<void> {
  const btn = $<HTMLButtonElement>('cnc-close-confirm');
  if (btn) btn.disabled = true;
  try {
    closeCloseConfirm();
    await applyMeetingStatus(true);
  } finally {
    if (btn) btn.disabled = false;
  }
}

function openDeleteConfirm(): void {
  if (!currentMeeting) return;
  $('cnc-delete-modal')?.classList.remove('hidden');
  // Фокус на безопасном действии: случайный Enter не должен стирать разговор.
  $('cnc-delete-cancel')?.focus();
}

function closeDeleteConfirm(): void {
  $('cnc-delete-modal')?.classList.add('hidden');
}

async function confirmDeleteMeeting(): Promise<void> {
  const meeting = currentMeeting;
  if (!meeting) return;
  const btn = $<HTMLButtonElement>('cnc-delete-confirm');
  if (btn) btn.disabled = true;
  try {
    const { ok, body } = await api<{ detail?: string }>(
      `/api/council/meetings/${encodeURIComponent(meeting.meeting_id)}`,
      { method: 'DELETE' },
    );
    if (!ok) {
      showToast(body.detail || 'Не удалось удалить встречу.', { variant: 'error' });
      return;
    }
    // Раунды удалённой встречи сторожу больше не интересны: сервер снял их
    // в `cancelled`, и без этого он через минуту сообщил бы о «сбое» раунда
    // встречи, которой уже нет.
    forgetMeetingRounds(meeting.meeting_id);
    closeDeleteConfirm();
    showToast('Встреча удалена вместе с материалами.');
    await loadCouncilView();
  } finally {
    if (btn) btn.disabled = false;
  }
}

function appendClientMessage(text: string): void {
  const feed = $('cnc-feed');
  if (!feed) return;
  feed.querySelector('.cnc-empty')?.remove();
  const el = document.createElement('div');
  el.className = 'cnc-msg cnc-msg--client';
  el.textContent = text;
  feed.appendChild(el);
  el.scrollIntoView({ block: 'end' });
}

function referenceTag(c: CouncilContribution): string {
  const ref = (c.references || [])[0];
  if (!ref) return '';
  const text = ref.kind === 'build_on'
    ? `↳ опирается на реплику ${ref.target_slot + 1}`
    : `⇄ другой взгляд на реплику ${ref.target_slot + 1}`;
  return `<span class="cnc-ref-tag">${esc(text)}</span>`;
}

function appendContribution(c: CouncilContribution): void {
  const feed = $('cnc-feed');
  if (!feed) return;
  feed.querySelector('.cnc-empty')?.remove();
  const speaker: CouncilSpeaker = (currentMeeting?.composition || [])
    .find((s) => s.speaker_avatar_ref === c.speaker_avatar_ref)
    || { speaker_avatar_ref: c.speaker_avatar_ref };
  const meta = seatMeta(speaker);
  const el = document.createElement('div');
  const isSynthesis = c.kind === 'synthesis';
  el.className = isSynthesis ? 'cnc-msg cnc-msg--speaker cnc-msg--synthesis' : 'cnc-msg cnc-msg--speaker';
  el.style.setProperty('--msg-hue', meta.hueVar);
  // Сведение позиций (Совет-2): говорит Глава Совета — Аватар из состава, его
  // имя и лицо (без Типа — Р-26); метка вместо ссылки на реплику.
  el.innerHTML = `<div class="cnc-msg-head">`
    + `<span class="cnc-msg-avatar">${esc(speakerInitial(speaker))}</span>`
    + `<span class="cnc-msg-name">${esc(speakerName(speaker))}</span>`
    + (isSynthesis ? '<span class="cnc-ref-tag">Глава Совета · сведение позиций</span>' : referenceTag(c))
    + '</div>'
    + `<div class="cnc-msg-body">${esc(c.content)}</div>`;
  feed.appendChild(el);
  el.scrollIntoView({ block: 'end' });
}

function showDeliberating(): void {
  const feed = $('cnc-feed');
  if (!feed || $('cnc-deliberating')) return;
  const el = document.createElement('div');
  el.id = 'cnc-deliberating';
  el.className = 'cnc-deliberating';
  el.innerHTML = '<i></i><i></i><i></i><span>Совет совещается…</span>';
  feed.appendChild(el);
  el.scrollIntoView({ block: 'end' });
  setSeatsSpeaking(true);
}

function hideDeliberating(): void {
  $('cnc-deliberating')?.remove();
  setSeatsSpeaking(false);
}

function updateComposerHint(): void {
  const hint = $('cnc-composer-hint');
  if (!hint) return;
  // У закрытой встречи своя подсказка: она объясняет заблокированную строку
  // реплики и важнее рассказа про сроки проверки куратором.
  if (isMeetingClosed(currentMeeting)) {
    hint.textContent = 'Встреча закрыта: материалы остались, новые реплики не начинаются. '
      + 'Откройте её заново, чтобы продолжить разговор.';
    return;
  }
  // Честное «K из M» (долг плана доводки Совета: «шесть за столом, двое
  // говорят» не было объяснено): состав шире числа реплик — не ошибка, а
  // устройство раунда, и клиент должен это видеть до отправки реплики.
  const parts: string[] = [];
  const seats = currentMeeting?.composition?.length ?? 0;
  const slots = councilConfig?.max_slots ?? 0;
  if (seats > 0 && slots > 0 && seats > slots) {
    parts.push(`В раунде выступят ${slots} из ${seats} участников — первые по очереди Совета.`);
  }
  if (councilConfig?.curator_review_enabled) {
    parts.push('Реплики Совета проходят проверку куратора — ответ может прийти не сразу.');
  }
  hint.textContent = parts.join(' ');
}

// ---------- Раунд ----------

async function sendRound(): Promise<void> {
  const input = $<HTMLTextAreaElement>('cnc-message');
  const meeting = currentMeeting;
  if (!input || !meeting || roundInFlight) return;
  // Закрытая встреча не начинает раундов. Поле ввода и так заблокировано, но
  // до отправки дотягиваются и горячей клавишей — молчаливая 4xx хуже отказа.
  if (isMeetingClosed(meeting)) {
    showToast('Встреча закрыта. Откройте её заново, чтобы продолжить разговор.', { variant: 'warning' });
    return;
  }
  const message = input.value.trim();
  if (!message) return;

  roundInFlight = true;
  const sendBtn = $<HTMLButtonElement>('cnc-send');
  if (sendBtn) sendBtn.disabled = true;
  appendClientMessage(message);
  input.value = '';
  input.style.height = '';
  showDeliberating();

  try {
    const { ok, status, body } = await api<{
      run_id?: string;
      status?: string;
      waiting_for?: string | null;
      contributions?: CouncilContribution[];
      detail?: string;
      code?: string;
    }>(`/api/council/meetings/${encodeURIComponent(meeting.meeting_id)}/rounds`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    hideDeliberating();

    if (!ok && status !== 202) {
      if (body.code === 'registration_required') showRegistrationPrompt();
      showToast(body.detail || 'Раунд Совета не удался. Попробуйте ещё раз.', { variant: 'error' });
      return;
    }

    (body.contributions || []).forEach((c) => appendContribution(c));
    // Совет заговорил — раздел итогов становится доступен (Совет-2).
    if ((body.contributions || []).length) void loadArtifacts();

    if (body.status === 'waiting' && body.waiting_for === 'consent_required' && body.run_id) {
      pendingConsentRunId = body.run_id;
      showConsent();
      return;
    }
    if (body.status === 'waiting' && body.waiting_for === 'curator_review') {
      showCuratorBanner();
      return;
    }
  } catch {
    hideDeliberating();
    showToast('Сеть недоступна — раунд не отправлен.', { variant: 'error' });
  } finally {
    roundInFlight = false;
    if (sendBtn) sendBtn.disabled = false;
  }
}

// ---------- Согласие на состав (ADR-14 §4) ----------

function showConsent(): void {
  const card = $('cnc-consent-card');
  const list = $('cnc-consent-list');
  if (!card || !list || !currentMeeting) return;
  const confirmed = new Set(currentMeeting.confirmed_speakers || []);
  const fresh = (currentMeeting.composition || []).filter((s) => !confirmed.has(s.speaker_avatar_ref));
  list.innerHTML = (fresh.length ? fresh : currentMeeting.composition || []).map((s) => {
    const meta = seatMeta(s);
    return `<span class="cnc-chip"><span class="cnc-chip-dot" style="--chip-hue:${meta.hueVar}">${esc(speakerInitial(s))}</span>`
      + `${esc(speakerName(s))}${meta.label ? ` · ${esc(meta.label)}` : ''}</span>`;
  }).join('');
  card.classList.remove('hidden');
  card.scrollIntoView({ block: 'end' });
}

function hideConsent(): void {
  $('cnc-consent-card')?.classList.add('hidden');
}

async function resolveConsent(outcome: 'granted' | 'denied'): Promise<void> {
  const runId = pendingConsentRunId;
  const meeting = currentMeeting;
  if (!runId || !meeting) return;
  hideConsent();
  if (outcome === 'granted') showDeliberating();

  const { ok, status, body } = await api<{ status?: string; waiting_for?: string | null; detail?: string }>(
    `/api/council/rounds/${encodeURIComponent(runId)}/consent`,
    { method: 'POST', body: JSON.stringify({ outcome }) },
  );
  hideDeliberating();
  pendingConsentRunId = null;

  if (!ok && status !== 202) {
    showToast(body.detail || 'Не удалось передать решение.', { variant: 'error' });
    return;
  }
  if (outcome === 'denied') {
    showToast('Раунд закрыт: новый состав не согласован.');
    return;
  }
  if (body.status === 'waiting' && body.waiting_for === 'curator_review') {
    showCuratorBanner();
    return;
  }
  // Согласие закрыло раунд целиком: реплики берём из материалов встречи —
  // ответ consent их не несёт.
  await openMeeting(meeting.meeting_id);
}

// ---------- Ожидание куратора ----------

function showCuratorBanner(): void {
  const feed = $('cnc-feed');
  if (!feed) return;
  // Пока человек смотрит на баннер — частый опрос: решение куратора должно
  // снимать ожидание на глазах, а не «когда-нибудь при следующем заходе».
  startCuratorWatch();
  if ($('cnc-curator-banner')) return;
  const el = document.createElement('div');
  el.id = 'cnc-curator-banner';
  el.className = 'cnc-curator-banner';
  el.innerHTML = '<span class="cnc-hourglass">⏳</span>'
    + '<span>Реплика на проверке у куратора. Как только проверка пройдёт, ответ появится прямо здесь.</span>';
  feed.appendChild(el);
  el.scrollIntoView({ block: 'end' });
}

function hideCuratorBanner(): void {
  $('cnc-curator-banner')?.remove();
  // Ждать больше нечего — частый опрос гасим здесь же, чтобы «мёртвый»
  // таймер не остался жить после закрытия ожидания.
  stopCuratorWatch();
}

// ---------- Модалка «Собрать Совет» ----------

async function ensureProfiles(): Promise<void> {
  if (profileCache.length) return;
  try {
    const { ok, body } = await api<AvatarProfileOption[]>('/api/avg-dialog/avatar-profiles');
    if (ok && Array.isArray(body)) profileCache = body;
  } catch { /* селект остаётся пустым — можно ввести имя вручную */ }
}

/**
 * Тип выбранного Аватара — прямо в селекте: он же его функция за столом.
 * Прежде здесь стояла сетка карточек «выберите функцию», и человек назначал
 * Неспящего Аватару, паспорт которого говорит «Хранитель».
 */
function fillAvatarSelect(): void {
  const sel = $<HTMLSelectElement>('cnc-avatar-select');
  if (!sel) return;
  // er_type_id здесь — только признак «личность есть» (за стол без неё нельзя);
  // сам Тип клиенту не показывается (Р-26, Хартия §6).
  const seatable = profileCache.filter((p) => p.er_type_id);
  sel.innerHTML = '<option value="">— выбрать аватара —</option>' + seatable
    .map((p) => `<option value="${esc(p.avatar_id)}">${esc(p.label || p.avatar_id)}</option>`)
    .join('');
  const hint = $('cnc-func-hint');
  if (hint && !seatable.length) {
    hint.textContent = 'Ни у одного Аватара нет личности — Совету некем говорить. Личность привязывает администратор.';
  }
}

function renderBuilderSpeakers(): void {
  const host = $('cnc-speakers-list');
  if (!host) return;
  if (!builderSpeakers.length) {
    host.innerHTML = '<div class="cnc-empty">Участников пока нет — добавьте хотя бы одного.</div>';
    return;
  }
  host.innerHTML = builderSpeakers.map((s, i) => {
    const meta = seatMeta(s);
    return `<div class="cnc-speaker-row">`
      + `<span class="cnc-chip-dot" style="--chip-hue:${meta.hueVar}">${esc(speakerInitial(s))}</span>`
      + `<span class="cnc-speaker-info"><span class="cnc-speaker-title">${esc(speakerName(s))}</span>`
      + (meta.label ? `<br><span class="cnc-speaker-sub">${esc(meta.label)}</span>` : '')
      + '</span>'
      + `<button type="button" class="cnc-remove-btn" data-remove="${i}" aria-label="Убрать участника">✕</button>`
      + '</div>';
  }).join('');
  host.querySelectorAll<HTMLElement>('[data-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      builderSpeakers.splice(Number(btn.dataset.remove), 1);
      renderBuilderSpeakers();
    });
  });
}

async function openBuilder(mode: 'create' | 'edit'): Promise<void> {
  builderMode = mode;
  builderSpeakers = mode === 'edit' ? [...(currentMeeting?.composition || [])] : [];

  const heading = $('cnc-builder-heading');
  if (heading) heading.textContent = mode === 'edit' ? 'Состав Совета' : 'Собрать Совет';
  const sub = $('cnc-builder-sub');
  if (sub) {
    // В закрытой встрече следующего раунда не будет, а значит не будет и
    // согласия: обещать его — врать. Состав менять не мешаем (встреча остаётся
    // читаемой и живой), но говорим, когда это вступит в силу.
    // Честно про число реплик (Р-22): состав может быть шире, чем выступит за
    // раунд, и узнать об этом клиент должен здесь, а не по ленте.
    const slots = councilConfig?.max_slots ?? 0;
    const slotsNote = slots > 0 && slots < MAX_SEATS
      ? ` В раунде выступают ${slots} — первые по очереди Совета.`
      : '';
    sub.textContent = mode !== 'edit'
      ? `Выберите до шести участников. Каждый говорит из своей функции — по очереди, обращаясь к вам.${slotsNote}`
      : isMeetingClosed(currentMeeting)
        ? 'Встреча закрыта: новый состав вступит в разговор, когда вы откроете её заново.'
        : 'Новые участники вступят после вашего согласия в следующем раунде.';
  }
  const titleWrap = $('cnc-title-wrap');
  titleWrap?.classList.toggle('hidden', mode === 'edit');
  const titleInput = $<HTMLInputElement>('cnc-title-input');
  if (titleInput && mode === 'create') titleInput.value = '';
  const submit = $('cnc-create-submit');
  if (submit) submit.textContent = mode === 'edit' ? 'Сохранить состав' : 'Создать встречу';

  await ensureProfiles();
  fillAvatarSelect();
  renderBuilderSpeakers();
  $('cnc-create-modal')?.classList.remove('hidden');
  titleInput?.focus();
}

/**
 * Состав в запросе — только ссылка на Аватара и имя за столом. Функция в теле
 * запроса привела бы к отказу схемы, и правильно: её задаёт паспорт, а не
 * клиент (ADR-008 §3.2 п. 3).
 */
function toRequestComposition(): Array<{ speaker_avatar_ref: string; display_name?: string }> {
  return builderSpeakers.map((s) => ({
    speaker_avatar_ref: s.speaker_avatar_ref,
    ...(s.display_name ? { display_name: s.display_name } : {}),
  }));
}

function closeBuilder(): void {
  $('cnc-create-modal')?.classList.add('hidden');
}

function addBuilderSpeaker(): void {
  const sel = $<HTMLSelectElement>('cnc-avatar-select');
  const nameInput = $<HTMLInputElement>('cnc-display-name');
  const ref = sel?.value || '';
  if (!ref) {
    showToast('Выберите аватара для нового участника.', { variant: 'warning' });
    return;
  }
  if (builderSpeakers.length >= MAX_SEATS) {
    showToast('В Совете не больше шести участников.', { variant: 'warning' });
    return;
  }
  const displayName = (nameInput?.value || '').trim();
  builderSpeakers.push({
    speaker_avatar_ref: ref,
    // Тип не передаётся и не показывается (Р-26); личность у выбранного есть — список уже отфильтрован.
    has_personality: true,
    ...(displayName ? { display_name: displayName } : {}),
  });
  if (nameInput) nameInput.value = '';
  if (sel) sel.value = '';
  renderBuilderSpeakers();
}

async function submitBuilder(): Promise<void> {
  if (!builderSpeakers.length) {
    showToast('Добавьте хотя бы одного участника.', { variant: 'warning' });
    return;
  }
  const submit = $<HTMLButtonElement>('cnc-create-submit');
  if (submit) submit.disabled = true;
  try {
    if (builderMode === 'edit' && currentMeeting) {
      const { ok, body } = await api<{ detail?: string }>(
        `/api/council/meetings/${encodeURIComponent(currentMeeting.meeting_id)}/composition`,
        { method: 'PUT', body: JSON.stringify({ composition: toRequestComposition() }) },
      );
      if (!ok) {
        showToast(body.detail || 'Не удалось сохранить состав.', { variant: 'error' });
        return;
      }
      closeBuilder();
      showToast(isMeetingClosed(currentMeeting)
        ? 'Состав обновлён. Он вступит в разговор, когда вы откроете встречу заново.'
        : 'Состав обновлён. Новые участники вступят после вашего согласия.');
      await openMeeting(currentMeeting.meeting_id);
      return;
    }

    const title = $<HTMLInputElement>('cnc-title-input')?.value.trim() || '';
    const { ok, status, body } = await api<{ meeting_id?: string; detail?: string; code?: string }>(
      '/api/council/meetings',
      { method: 'POST', body: JSON.stringify({ title, composition: toRequestComposition() }) },
    );
    if (!ok) {
      if (status === 403 && body.code === 'registration_required') {
        showRegistrationPrompt();
        return;
      }
      showToast(body.detail || 'Не удалось создать встречу.', { variant: 'error' });
      return;
    }
    closeBuilder();
    if (body.meeting_id) await openMeeting(body.meeting_id);
  } finally {
    if (submit) submit.disabled = false;
  }
}

// ---------- Сторож решений куратора (C-2) ----------

/**
 * Куратор отвечает когда угодно: через минуту или через сутки. До C-2 клиент
 * видел баннер «на проверке» — и всё; об одобрении он узнавал, только если сам
 * догадывался вернуться во встречу, а одобренная реплика могла пролежать
 * непрочитанной сутки.
 *
 * Поэтому два темпа опроса одного и того же дешёвого маршрута
 * (`/api/council/rounds/pending` — статусы раундов без единой буквы реплик):
 * частый, пока человек сидит в зале встречи и видит ожидание, и редкий фоновый
 * — по образцу `startDeferredWatch` отложенной услуги, чтобы решение нашло
 * человека и в другом разделе. Частый гаснет при выходе из встречи и из самого
 * раздела «Совет» (`leaveCouncilView`), оба — при выходе из аккаунта и при
 * выключенной услуге: «мёртвый» таймер греет сервер зря.
 */
const CURATOR_POLL_MS = 8_000;
const COUNCIL_WATCH_MS = 60_000;

interface CouncilPendingRound {
  run_id: string;
  meeting_id: string;
  status: string;
  waiting_for: string | null;
  closed_reason: string | null;
  /**
   * Сколько реплик раунда действительно дошло до клиента. Статус run'а на этот
   * вопрос не отвечает: куратор отклонил реплику — узел закрытия всё равно
   * пишет `completed`, и по статусу выходило бы «Совет ответил» при пустой
   * ленте. Поле необязательное: ответ сервера прежней версии просто оставит
   * прежнее поведение, а не соврёт про «реплики не будет».
   */
  emitted_count?: number;
  updated_at?: string;
}

let curatorPollTimer: ReturnType<typeof setInterval> | null = null;
let councilWatchTimer: ReturnType<typeof setInterval> | null = null;
let roundsPollInFlight = false;
/**
 * Последнее известное состояние раунда: решение ловим по уходу из ожидания
 * куратора. Чего именно ждал раунд, помним не зря — согласие на состав даёт
 * сам человек и ответ на своё нажатие видит сразу, так что о таком закрытии
 * сторожу говорить нечего.
 */
const knownRounds = new Map<string, {
  meetingId: string; status: string; waitingFor: string | null; updatedAt: string;
}>();

/**
 * Забыть раунды встречи, которую человек только что закрыл или удалил. Сервер
 * снимает их через `ogRunner.cancel` — это смена статуса на `cancelled` без
 * причины закрытия, и сторож, помнящий их ожидающими, через десятки секунд
 * сообщал бы о «сбое» раунда по осознанному действию человека, а при удалении
 * — про встречу, которой уже нет. Забытый раунд для сторожа новый, а первый
 * опрос про незнакомый раунд — не новость.
 */
function forgetMeetingRounds(meetingId: string): void {
  for (const [runId, known] of knownRounds) {
    if (known.meetingId === meetingId) knownRounds.delete(runId);
  }
}

function startCuratorWatch(): void {
  if (curatorPollTimer || !state.authToken) return;
  curatorPollTimer = setInterval(() => void refreshCouncilRounds(), CURATOR_POLL_MS);
}

function stopCuratorWatch(): void {
  if (!curatorPollTimer) return;
  clearInterval(curatorPollTimer);
  curatorPollTimer = null;
}

/**
 * Фоновый сторож: зовётся после входа рядом с `refreshCouncilNav`. Без гейта
 * куратора ждать нечего — раунд закрывается в том же запросе, поэтому сторож
 * даже не поднимается; ожидание, возникшее в открытой встрече, всё равно
 * заводит свой частый опрос само (см. `showCuratorBanner`).
 */
export async function startCouncilWatch(): Promise<void> {
  stopCouncilWatch();
  if (!state.authToken) return;
  if (!councilConfig) {
    try {
      const { ok, body } = await api<CouncilConfig>('/api/council/config');
      councilConfig = ok ? body : null;
    } catch { councilConfig = null; }
  }
  if (!councilConfig?.enabled || !councilConfig.curator_review_enabled) return;

  await refreshCouncilRounds();
  councilWatchTimer = setInterval(() => void refreshCouncilRounds(), COUNCIL_WATCH_MS);
}

/** Уход из раздела «Совет»: частый опрос дальше греет сервер впустую. */
export function leaveCouncilView(): void {
  stopCuratorWatch();
}

export function stopCouncilWatch(): void {
  stopCuratorWatch();
  if (councilWatchTimer) {
    clearInterval(councilWatchTimer);
    councilWatchTimer = null;
  }
  knownRounds.clear();
}

/**
 * `freshFeed` — лента встречи только что прочитана (опрос идёт из
 * `openMeeting`): перечитывать её из-под себя незачем, да и рекурсия ни к чему.
 */
async function refreshCouncilRounds(opts: { freshFeed?: boolean } = {}): Promise<void> {
  if (!state.authToken) {
    stopCouncilWatch();
    return;
  }
  // Живой раунд сам покажет свой исход ответом на POST — не мешаем ему,
  // перечитывая ленту из-под руки.
  if (roundsPollInFlight || roundInFlight) return;
  roundsPollInFlight = true;
  try {
    const { ok, status, body } = await api<{ rounds?: CouncilPendingRound[] }>('/api/council/rounds/pending');
    if (!ok) {
      // Услуга выключена (503) или кабинет этому аккаунту не положен (403):
      // сторожить нечего, опрос гасим до следующего входа.
      if (status === 403 || status === 503) stopCouncilWatch();
      return;
    }
    for (const round of body.rounds || []) await applyRoundState(round, opts);
  } catch {
    /* сеть моргнула — разберётся следующий тик */
  } finally {
    roundsPollInFlight = false;
  }
}

async function applyRoundState(
  round: CouncilPendingRound,
  opts: { freshFeed?: boolean } = {},
): Promise<void> {
  const known = knownRounds.get(round.run_id);
  const updatedAt = String(round.updated_at || '');
  knownRounds.set(round.run_id, {
    meetingId: round.meeting_id, status: round.status, waitingFor: round.waiting_for, updatedAt,
  });
  const inChamber = currentMeeting?.meeting_id === round.meeting_id;

  if (round.status === 'waiting') {
    if (!inChamber) return;
    // Согласие на состав ждёт человека ровно так же, как проверка куратора, а
    // карточку с кнопками держит только память вкладки: перерисовка ленты
    // (решение куратора по соседнему раунду, перезагрузка страницы) стирала её
    // насовсем, и раунд висел до срока. Восстанавливаем — как баннер куратора.
    if (round.waiting_for === 'consent_required') {
      pendingConsentRunId = round.run_id;
      showConsent();
      return;
    }
    if (round.waiting_for !== 'curator_review') return;
    // Ожидание переживает перезагрузку страницы, а материалы встречи о нём
    // молчат — непроверенной реплики там нет; поэтому баннер восстанавливаем
    // здесь. Сдвиг времени раунда при том же ожидании значит, что предыдущая
    // реплика уже одобрена и эмитирована: перечитываем ленту.
    if (known && known.updatedAt !== updatedAt && !opts.freshFeed) await openMeeting(round.meeting_id);
    showCuratorBanner();
    return;
  }

  // Раунд ушёл из ожидания куратора — только об этом сторож и говорит. Первый
  // опрос — не новость: раунд, закрытый ещё до его запуска, человек либо уже
  // видел, либо увидит в материалах встречи.
  if (known?.status !== 'waiting' || known.waitingFor !== 'curator_review') return;
  hideCuratorBanner();
  if (inChamber && !opts.freshFeed) await openMeeting(round.meeting_id);
  announceRoundOutcome(round, inChamber);
}

/**
 * Исход не всегда «ответ пришёл»: куратор мог закрыть раунд, отклонить реплику
 * или не успеть в срок — тогда реплики не будет вовсе. Говорим об этом прямо и
 * мягко и **не обещаем возврата денег**: работа уже сделана и оплачена
 * провайдеру (решение владельца 2026-08-13).
 *
 * Судим по факту эмиссии, а не по статусу run'а: отклонение слота
 * (`reject_slot`) причины закрытия не пишет, узел закрытия ставит `completed`
 * — и «Проверка пройдена — Совет ответил» звучало над пустой лентой.
 */
function announceRoundOutcome(round: CouncilPendingRound, inChamber: boolean): void {
  if (round.closed_reason === 'curator_closed') {
    showToast('Куратор закрыл раунд: этой реплики Совет не скажет. Вы можете спросить снова — другими словами.',
      { variant: 'warning', duration: 12000 });
    return;
  }
  if (round.closed_reason === 'curator_timeout') {
    showToast('Проверка реплики не прошла в срок, и раунд закрылся: ответа по нему не будет. Спросите Совет ещё раз.',
      { variant: 'warning', duration: 12000 });
    return;
  }
  // Снятый раунд — это не сбой, а чьё-то решение: так выглядит и закрытие
  // встречи самим человеком (раунды такой встречи сторож забывает, см.
  // forgetMeetingRounds), и снятие раунда администратором.
  if (round.status === 'cancelled') {
    showToast('Раунд Совета остановлен: ответа по нему не будет. Спросите Совет ещё раз.',
      { variant: 'warning', duration: 12000 });
    return;
  }
  if (round.emitted_count === 0) {
    showToast('Проверка закончилась ничем: эту реплику куратор не пропустил, и Совет её не скажет. '
      + 'Спросите ещё раз — другими словами.', { variant: 'warning', duration: 12000 });
    return;
  }
  if (round.status !== 'completed') {
    showToast('Раунд Совета не завершился. Попробуйте задать вопрос ещё раз.', { variant: 'error' });
    return;
  }
  showToast(inChamber
    ? 'Проверка пройдена — Совет ответил.'
    : 'Совет ответил: реплика прошла проверку и ждёт вас в материалах встречи.',
  { duration: 12000 });
}

// ---------- Инициализация ----------

export function initCouncil(): void {
  $('cnc-create-open')?.addEventListener('click', () => void openBuilder('create'));
  $('cnc-edit-composition')?.addEventListener('click', () => void openBuilder('edit'));
  $('cnc-artifacts-request')?.addEventListener('click', () => void requestArtifacts());
  $('cnc-back')?.addEventListener('click', () => {
    showHall();
    void loadCouncilView();
  });
  $('cnc-create-cancel')?.addEventListener('click', closeBuilder);
  $('cnc-create-backdrop')?.addEventListener('click', closeBuilder);
  $('cnc-rename-open')?.addEventListener('click', openRename);
  $('cnc-rename-cancel')?.addEventListener('click', closeRename);
  $('cnc-rename-backdrop')?.addEventListener('click', closeRename);
  $('cnc-rename-save')?.addEventListener('click', () => void saveRename());
  $('cnc-toggle-status')?.addEventListener('click', () => void toggleMeetingStatus());
  $('cnc-close-cancel')?.addEventListener('click', closeCloseConfirm);
  $('cnc-close-backdrop')?.addEventListener('click', closeCloseConfirm);
  $('cnc-close-confirm')?.addEventListener('click', () => void confirmCloseMeeting());
  $('cnc-delete-open')?.addEventListener('click', openDeleteConfirm);
  $('cnc-delete-cancel')?.addEventListener('click', closeDeleteConfirm);
  $('cnc-delete-backdrop')?.addEventListener('click', closeDeleteConfirm);
  $('cnc-delete-confirm')?.addEventListener('click', () => void confirmDeleteMeeting());
  $<HTMLInputElement>('cnc-rename-input')?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveRename();
    }
  });
  $('cnc-add-speaker')?.addEventListener('click', addBuilderSpeaker);
  $('cnc-create-submit')?.addEventListener('click', () => void submitBuilder());
  $('cnc-send')?.addEventListener('click', () => void sendRound());
  $('cnc-consent-grant')?.addEventListener('click', () => void resolveConsent('granted'));
  $('cnc-consent-deny')?.addEventListener('click', () => void resolveConsent('denied'));

  const input = $<HTMLTextAreaElement>('cnc-message');
  input?.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 160) + 'px';
  });
  input?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !window.matchMedia('(pointer: coarse)').matches) {
      e.preventDefault();
      void sendRound();
    }
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    closeBuilder();
    closeRename();
    closeCloseConfirm();
    closeDeleteConfirm();
  });
}
